import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useCallback, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';

import { FinancialChartData } from '~hooks/query/company/useCompanyFinancialChart';
import { getTransformMoneyUnit } from '~utils/formatted/string';
import { isEqual } from '~utils/lodash';

import GuideMessage from '../GuideMessage';
import { useDisplayTrigger } from '../GuideMessage/hooks';
import { useChartProps } from '../hooks';
import Legends from '../Legends';
import TooltipFinancial, { TooltipFinancialProps } from '../TooltipFinancial';
import * as S from './style';

interface Props {
  data: FinancialChartData[];
  isEmpty?: boolean;
}

ChartJS.register(
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  PointElement,
  LineElement,
  Tooltip,
);

export default function ChartContents({ data: originData, isEmpty }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [tooltipProps, setTooltipProps] = useState<TooltipFinancialProps>();
  const isDisplyGuide = useDisplayTrigger(wrapperRef);

  const externalTooltipHandler = useCallback(
    (context: any) => {
      if (context.tooltip.opacity === 0) {
        // @ts-ignore
        setTooltipProps((prevState) => ({ ...prevState, $opacity: 0 }));

        return;
      }

      const { tooltip } = context;
      const { xAlign, dataPoints } = tooltip;
      const { x, y } = dataPoints[0].element;
      const [year] = tooltip.title;

      if (dataPoints[2] === undefined) {
        setTooltipProps(undefined);

        return;
      }

      const { salesAmount, operatingIncome, netIncome } = dataPoints[0].raw;

      const newTooltipProps: TooltipFinancialProps = {
        $x: x,
        $y: y,
        $opacity: 1,
        xAlign,
        salesAmount: getTransformMoneyUnit(salesAmount, true),
        operatingIncome: getTransformMoneyUnit(operatingIncome, true),
        netIncome: getTransformMoneyUnit(netIncome, true),
        year,
      };

      if (!isEqual(tooltipProps, newTooltipProps)) setTooltipProps(newTooltipProps);
    },
    [tooltipProps, originData],
  );

  const { data, options, plugins, isInteracted } = useChartProps({
    originData,
    externalTooltipHandler,
    isEmpty,
  });

  return (
    <S.Wrapper ref={wrapperRef}>
      <Legends />
      <S.ChartContainer>
        <>
          {/* @ts-ignore */}
          <Line data={data} options={options} plugins={plugins} />
          {tooltipProps && <TooltipFinancial {...tooltipProps} />}
        </>
        {isEmpty && <S.NoData>재무제표 정보가 없습니다.</S.NoData>}

        {!isEmpty && isDisplyGuide && !isInteracted && <GuideMessage />}
      </S.ChartContainer>
    </S.Wrapper>
  );
}
